<template>
  <div class="report-overlay" @click.self="close">
    <div class="report-card">
      <header class="report-header">
        <button class="icon-btn" @click="$emit('close')" aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
        <h2 class="report-title">Report publication</h2>
        <div class="spacer"></div>
      </header>

      <!-- 1. Introduction with the reported user's name -->
      <p class="report-intro">
        You're reporting a publication from
        <strong class="reported-name">{{ reportedUserName }}</strong>.
        Reports are reviewed by our team and kept confidential.
      </p>

      <form class="report-form" @submit.prevent="handleSubmit">
        <!-- 2. Publication link -->
        <div class="field">
          <label class="field-label" for="pub-link">Publication link</label>
          <input
            id="pub-link"
            v-model.trim="form.link"
            type="url"
            class="field-input"
            placeholder="https://app.example.com/post/123"
            required
          />
          <p v-if="errors.link" class="field-error">{{ errors.link }}</p>
        </div>

        <!-- 3. Report type dropdown -->
        <div class="field">
          <label class="field-label" for="reason">Reason for report</label>
          <div class="select-wrap">
            <select id="reason" v-model="form.reason" class="field-input select" required>
              <option value="" disabled>Select a reason</option>
              <option v-for="reason in reasons" :key="reason.value" :value="reason.value">
                {{ reason.label }}
              </option>
            </select>
            <svg class="select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p v-if="errors.reason" class="field-error">{{ errors.reason }}</p>
        </div>

        <!-- 4. Description -->
        <div class="field">
          <label class="field-label" for="description">
            Description
            <span class="field-optional">optional</span>
          </label>
          <textarea
            id="description"
            v-model.trim="form.description"
            class="field-input textarea"
            rows="4"
            maxlength="500"
            placeholder="Add any details that will help us review this report..."
          ></textarea>
          <p class="char-count">{{ form.description.length }}/500</p>
        </div>

        <!-- 5. Submit -->
        <button type="submit" class="submit-btn" :disabled="submitting">
          <span v-if="!submitting && !submitSuccesfull">Submit report</span>
          <span v-if="submitting && !submitSuccesfull" class="submit-loading">
            <svg class="spinner" width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="3" stroke-opacity="0.25"/>
              <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            </svg>
            Sending...
          </span>
          <span v-if="submitSuccesfull">Success</span>
        </button>

        <p v-if="submitError" class="submit-error">{{ submitError }}</p>
      </form>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { reactive, ref, inject } from 'vue'

const axios = inject('axios')

const props = defineProps({
  reportedUserName: {
    type: String,
    required: true
  },
  reportedUserId: {
    type: [String, Number],
    default: null
  },
  prefillLink: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['close'])
function close () {
    submitSuccesfull.value = false
    emit('close')
}
const reasons = [
  { value: 'copyright', label: 'Copyright violation' },
  { value: 'hatred', label: 'Hatred or discrimination' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'nudity', label: 'Nudity or sexual content' },
  { value: 'violence', label: 'Violence or dangerous acts' },
  { value: 'misinformation', label: 'False information' },
  { value: 'spam', label: 'Spam or scam' },
  { value: 'other', label: 'Something else' }
]

const form = reactive({
  link: props.prefillLink,
  reason: '',
  description: ''
})

const errors = reactive({
  link: '',
  reason: ''
})

const submitting = ref(false)
const submitError = ref('')
const submitSuccesfull = ref(false)

function validate() {
  errors.link = ''
  errors.reason = ''
  let valid = true

  if (!form.link) {
    errors.link = 'Please add a link to the publication.'
    valid = false
  } else {
    try {
      new URL(form.link)
    } catch {
      errors.link = 'Please enter a valid URL.'
      valid = false
    }
  }

  if (!form.reason) {
    errors.reason = 'Please choose a reason.'
    valid = false
  }

  return valid
}

async function handleSubmit() {
  submitError.value = ''
  if(submitSuccesfull.value) return
  if (!validate()) return

  submitting.value = true
  try {
    // Replace with your real report endpoint
    await new Promise((resolve) => setTimeout(resolve, 900))
    
    await axios.post('report.php',{
      type:'save',
      reportedUserId: props.reportedUserId,
      link: form.link,
      reason: form.reason,
      description: form.description
    })
    submitSuccesfull.value = true
    // emit('submitted')
 
  } catch (err) {
    submitError.value = 'Something went wrong. Please try again.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.report-overlay {
  position: fixed;
  inset: 0;
  background: rgba(4, 7, 15, 0.72);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 2000;
}

@media (min-width: 640px) {
  .report-overlay {
    align-items: center;
  }
}

.report-card {
  width: 100%;
  max-width: 440px;
  max-height: 90vh;
  overflow-y: auto;
  background: #0d1220;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 20px 20px 0 0;
  padding: 20px 20px 28px;
  color: #e6ebf3;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.5);
}

@media (min-width: 640px) {
  .report-card {
    border-radius: 20px;
  }
}

.report-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.1);
  border: none;
  color: #9aa7bd;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.icon-btn:hover {
  background: rgba(148, 163, 184, 0.18);
  color: #e6ebf3;
}

.report-title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.spacer {
  width: 32px;
}

.report-intro {
  font-size: 13.5px;
  line-height: 1.5;
  color: #9aa7bd;
  margin: 4px 4px 20px;
}

.reported-name {
  color: #22d3ee;
  font-weight: 600;
}

.report-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.field-label {
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #9aa7bd;
  display: flex;
  justify-content: space-between;
}

.field-optional {
  text-transform: none;
  font-weight: 400;
  color: #5b6780;
}

.field-input {
  width: 100%;
  background: #131a2c;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 14.5px;
  color: #e6ebf3;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
  box-sizing: border-box;
}

.field-input::placeholder {
  color: #4d5872;
}

.field-input:focus {
  border-color: #22d3ee;
  box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.15);
}

.select-wrap {
  position: relative;
}

.select {
  appearance: none;
  cursor: pointer;
  padding-right: 36px;
}

.select-chevron {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7690;
  pointer-events: none;
}

.textarea {
  resize: vertical;
  min-height: 92px;
  line-height: 1.5;
}

.char-count {
  align-self: flex-end;
  font-size: 11px;
  color: #4d5872;
  margin: 0;
}

.field-error {
  font-size: 12px;
  color: #fb7185;
  margin: 0;
}

.submit-btn {
  margin-top: 4px;
  width: 100%;
  padding: 13px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #ec4899, #22d3ee);
  color: #06090f;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease;
}

.submit-btn:hover:not(:disabled) {
  opacity: 0.92;
}

.submit-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.submit-error {
  text-align: center;
  font-size: 12.5px;
  color: #fb7185;
  margin: 0;
}
</style>